(() => {
  const selectors = {
    drawer: '[data-cart-drawer]',
    panel: '.cart-drawer__panel',
    open: '[data-cart-drawer-open]',
    close: '[data-cart-drawer-close]',
    quantity: '[data-cart-drawer-quantity]',
    increment: '[data-cart-drawer-increment]',
    decrement: '[data-cart-drawer-decrement]',
    remove: '[data-cart-drawer-remove]',
    add: '[data-cart-drawer-add]',
    timer: '[data-cart-drawer-timer]',
    timerValue: '[data-cart-drawer-timer-value]',
    productForm: 'form[action*="/cart/add"]'
  };

  let cartTimerInterval = null;
  let cartTimerClearing = false;

  const getDrawer = () => document.querySelector(selectors.drawer);

  const clearCartTimer = () => {
    if (cartTimerInterval) {
      window.clearInterval(cartTimerInterval);
      cartTimerInterval = null;
    }

    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith('cart-drawer-timer-'))
      .forEach((key) => {
        window.sessionStorage.removeItem(key);
      });
  };

  const formatRemainingTime = (remainingSeconds) => {
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    if (minutes <= 0) {
      return `${seconds} s`;
    }

    return `${minutes}min ${String(seconds).padStart(2, '0')}s`;
  };

  const getTimerText = (template, remainingSeconds) => {
    const formattedTime = formatRemainingTime(remainingSeconds);
    const durationPattern = /\d+\s*min(?:\s*\d{1,2}\s*s)?\.?/i;

    if (durationPattern.test(template)) {
      return template.replace(durationPattern, formattedTime);
    }

    return `${template} ${formattedTime}`;
  };

  const initCartTimer = () => {
    const drawer = getDrawer();
    const timer = drawer ? drawer.querySelector(selectors.timer) : null;
    const value = timer ? timer.querySelector(selectors.timerValue) : null;
    const itemCount = drawer ? Number(drawer.dataset.cartItemCount || 0) : 0;

    if (cartTimerInterval) {
      window.clearInterval(cartTimerInterval);
      cartTimerInterval = null;
    }

    if (itemCount <= 0) {
      clearCartTimer();
      return;
    }

    if (!timer || !value) {
      return;
    }

    const durationMinutes = Number(timer.dataset.timerDuration || 0);

    if (!durationMinutes) {
      return;
    }

    const storageKey = `cart-drawer-timer-${durationMinutes}`;
    const durationMs = durationMinutes * 60 * 1000;
    const now = Date.now();
    let deadline = Number(window.sessionStorage.getItem(storageKey) || 0);
    const template = value.textContent.trim();

    if (!deadline || deadline <= now) {
      deadline = now + durationMs;
      window.sessionStorage.setItem(storageKey, String(deadline));
    }

    const render = () => {
      const remainingSeconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      value.textContent = getTimerText(template, remainingSeconds);

      if (remainingSeconds <= 0) {
        clearCartTimer();

        if (!cartTimerClearing) {
          cartTimerClearing = true;

          clearCart()
            .then(refreshDrawer)
            .finally(() => {
              cartTimerClearing = false;
            });
        }
      }
    };

    render();
    cartTimerInterval = window.setInterval(render, 1000);
  };

  const setScrollbarCompensation = () => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--cart-drawer-scrollbar-width', `${Math.max(scrollbarWidth, 0)}px`);
  };

  const clearScrollbarCompensation = () => {
    document.documentElement.style.removeProperty('--cart-drawer-scrollbar-width');
  };

  const setDrawerState = (open) => {
    const drawer = getDrawer();

    if (!drawer) {
      return;
    }

    if (open) {
      setScrollbarCompensation();
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');

      window.requestAnimationFrame(() => {
        drawer.classList.add('is-open');
      });

      const panel = drawer.querySelector(selectors.panel);
      if (panel) {
        panel.focus();
      }

      return;
    }

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-open');

    window.setTimeout(() => {
      if (!drawer.classList.contains('is-open')) {
        drawer.hidden = true;
        clearScrollbarCompensation();
      }
    }, 380);
  };

  const updateHeaderCartCount = (itemCount) => {
    document.querySelectorAll('.header__cart').forEach((cartLink) => {
      let count = cartLink.querySelector('.header__cart-count');

      if (itemCount > 0 && !count) {
        count = document.createElement('span');
        count.className = 'header__cart-count';
        cartLink.appendChild(count);
      }

      if (count) {
        count.textContent = itemCount;
        count.hidden = itemCount <= 0;
      }
    });
  };

  const fetchCart = () => fetch('/cart.js', {
    headers: {
      Accept: 'application/json'
    }
  }).then((response) => response.json());

  const clearCart = () => fetch('/cart/clear.js', {
    method: 'POST',
    headers: {
      Accept: 'application/json'
    }
  });

  const refreshDrawer = () => {
    const drawer = getDrawer();

    if (!drawer || !drawer.dataset.sectionId) {
      return Promise.resolve();
    }

    const wasOpen = drawer.classList.contains('is-open') && !drawer.hidden;
    const url = `${window.location.pathname}?sections=${drawer.dataset.sectionId}`;

    return fetch(url)
      .then((response) => response.json())
      .then((sections) => {
        const html = sections[drawer.dataset.sectionId];

        if (!html) {
          return;
        }

        const template = document.createElement('template');
        template.innerHTML = html.trim();
        const nextDrawer = template.content.querySelector(selectors.drawer);

        if (nextDrawer) {
          if (wasOpen) {
            nextDrawer.hidden = false;
            nextDrawer.setAttribute('aria-hidden', 'false');
            nextDrawer.classList.add('is-open');
          }

          drawer.replaceWith(nextDrawer);
          initCartTimer();
        }
      })
      .then(fetchCart)
      .then((cart) => {
        updateHeaderCartCount(cart.item_count);
      });
  };

  const changeLine = (line, quantity) => fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ line, quantity })
  });

  const addVariant = (variantId) => fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ id: Number(variantId), quantity: 1 })
  });

  const addForm = (form) => fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      Accept: 'application/json'
    },
    body: new FormData(form)
  });

  const getSelectedAddonItems = (form) => {
    const productSection = form.closest('.product');

    if (!productSection) {
      return [];
    }

    return Array.from(productSection.querySelectorAll('[data-addon-action]'))
      .map((action) => ({
        id: Number(action.dataset.addonVariantId),
        quantity: Number(action.dataset.addonQuantity || 0)
      }))
      .filter((item) => item.id && item.quantity > 0);
  };

  const addItems = (items) => fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ items })
  });

  const submitProductForm = (form) => {
    const submitButton = form.querySelector('[type="submit"]');
    const addonItems = getSelectedAddonItems(form);

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
    }

    const addRequest = addonItems.length > 0 ? addItems(addonItems) : addForm(form);

    return addRequest
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to add product to cart.');
        }
      })
      .then(refreshDrawer)
      .then(() => {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
        }

        setDrawerState(true);
      })
      .catch(() => {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
        }
      });
  };

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest(selectors.open);
    const closeButton = event.target.closest(selectors.close);
    const incrementButton = event.target.closest(selectors.increment);
    const decrementButton = event.target.closest(selectors.decrement);
    const removeButton = event.target.closest(selectors.remove);
    const addButton = event.target.closest(selectors.add);

    if (openButton) {
      event.preventDefault();
      setDrawerState(true);
      return;
    }

    if (closeButton) {
      event.preventDefault();
      setDrawerState(false);
      return;
    }

    if (incrementButton || decrementButton || removeButton) {
      event.preventDefault();

      const button = incrementButton || decrementButton || removeButton;
      const line = Number(button.dataset.line);
      const input = document.querySelector(`${selectors.quantity}[data-line="${line}"]`);
      const currentQuantity = input ? Number(input.value || 0) : 0;
      let nextQuantity = currentQuantity;

      if (incrementButton) {
        nextQuantity += 1;
      } else if (decrementButton) {
        nextQuantity = Math.max(0, currentQuantity - 1);
      } else {
        nextQuantity = 0;
      }

      button.disabled = true;
      changeLine(line, nextQuantity)
        .then(refreshDrawer)
        .catch(() => {
          button.disabled = false;
        });
      return;
    }

    if (addButton) {
      event.preventDefault();

      addButton.disabled = true;
      addVariant(addButton.dataset.variantId)
        .then(refreshDrawer)
        .catch(() => {
          addButton.disabled = false;
        });
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest(selectors.quantity);

    if (!input) {
      return;
    }

    const line = Number(input.dataset.line);
    const quantity = Math.max(0, Number(input.value || 0));

    input.disabled = true;
    changeLine(line, quantity)
      .then(refreshDrawer)
      .catch(() => {
        input.disabled = false;
      });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(selectors.productForm);

    if (!form) {
      return;
    }

    event.preventDefault();
    submitProductForm(form);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setDrawerState(false);
    }
  });

  initCartTimer();
})();
